import { Auth, type ProcessEventResponse } from "@saasbase-io/loginflow-websdk";
import { html, LitElement, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { NAMESPACE } from "@/constants";
import { genRandSeed } from "@/utils";

import { DEFAULT_LINK_PROPS as DEFAULT_PROPS } from "./link.defs";
import type { LinkPart, LinkService, SbLinkProps as Props } from "./link.types";

/**
 * Link button with an optional inline prompt, for navigational and
 * action-driven UI flows.
 *
 * @part link - div: root, contains both subparts
 * @part link-button - rtg-button: main clickable element
 * @part link-prompt - p: optional inline text, preceding the button part
 *
 * @event sb-link:click - Fired when the link button is clicked, before any auth call.
 *  Detail: `{ id: string; buttonId: string; service: "auth" | "other"; event?: string }`
 * @event sb-link:success - Fired after the auth call resolves successfully (only when `service="auth"`).
 *  Detail: `{ id: string; buttonId: string; event: string; result: ProcessEventResponse }`
 * @event sb-link:error - Fired when the auth call fails (only when `service="auth"`).
 *  Detail: `{ id: string; buttonId: string; event: string; message: string }`
 */
@customElement("sb-link")
export class SbLink extends LitElement implements Props {
  static readonly ROOT = "link";
  static readonly BUTTON = "button";
  static readonly PROMPT = "prompt";

  static readonly TAG = `${NAMESPACE}-${SbLink.ROOT}`;

  static readonly CLICK_EVENT = `${SbLink.TAG}:click`;
  static readonly SUCCESS_EVENT = `${SbLink.TAG}:success`;
  static readonly ERROR_EVENT = `${SbLink.TAG}:error`;

  static readonly PARTS: Record<LinkPart, string> = {
    root: SbLink.ROOT,
    button: `${SbLink.ROOT}-${SbLink.BUTTON}`,
    prompt: `${SbLink.ROOT}-${SbLink.PROMPT}`,
  };

  /* ------------------------------------------------------------ */

  /**
   * Substring used in generated part IDs.
   */
  @property({ type: String })
  seed = genRandSeed();

  /**
   * Custom ID used as the root part ID and as the base of subpart IDs.
   */
  @property({ type: String, attribute: "custom-id" })
  customId?: string;

  /**
   * Text used as the label of the link button part.
   */
  private _label = DEFAULT_PROPS.label;

  @property({ type: String })
  get label() {
    return this._label;
  }

  set label(value: string) {
    const old = this._label;
    this._label = value.length ? value : DEFAULT_PROPS.label;
    this.requestUpdate("label", old);
  }

  /**
   * Helper text rendered before and inline with the button.
   */
  @property({ type: String })
  prompt?: string;

  /**
   * Service which handles the `event` (if provided) on click.
   */
  @property({ type: String })
  service: LinkService = DEFAULT_PROPS.service;

  /**
   * Event identifier forwarded to the specified `service` on click.
   * Certain services may not handle events.
   */
  @property({ type: String })
  event?: string;

  /**
   * Disables the link button, preventing interaction.
   */
  @property({ type: Boolean })
  disabled?: boolean;

  /**
   * Manual alternative to the private `_loading` state.
   */
  @property({ type: Boolean })
  loading?: boolean;

  /* ---------------------------------------- */

  /**
   * Indicates whether the component is dispatching or emitting an event.
   */
  @state()
  private _loading = false;

  /* ------------------------------------------------------------ */

  protected createRenderRoot() {
    return this;
  }

  /* ------------------------------------------------------------ */

  get rootId() {
    return this.customId ?? `${SbLink.TAG}-${this.seed}`;
  }

  get buttonId() {
    return `${this.rootId}-${SbLink.BUTTON}`;
  }

  get promptId() {
    return `${this.rootId}-${SbLink.PROMPT}`;
  }

  get isLoading() {
    return this.loading || this._loading;
  }

  get isDisabled() {
    return this.disabled || this.isLoading;
  }

  /* ------------------------------------------------------------ */

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this._handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this._handleClick);
  }

  /* ------------------------------------------------------------ */

  private _handleClick = async () => {
    if (this.isDisabled) return;
    this._loading = true;

    const { rootId: id, buttonId, service, event } = this;

    this.dispatchEvent(
      new CustomEvent(SbLink.CLICK_EVENT, {
        detail: { id, buttonId, service, event },
        bubbles: true,
        composed: true,
      }),
    );

    try {
      if (event && service === "auth") {
        const result: ProcessEventResponse = await Auth.processLoginflowEvent({
          event,
        });
        this.dispatchEvent(
          new CustomEvent(SbLink.SUCCESS_EVENT, {
            detail: { id, buttonId, event, result },
            bubbles: true,
            composed: true,
          }),
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      this.dispatchEvent(
        new CustomEvent(SbLink.ERROR_EVENT, {
          detail: { id, buttonId, event, message },
          bubbles: true,
          composed: true,
        }),
      );
    } finally {
      this._loading = false;
    }
  };

  /* ------------------------------------------------------------ */

  render() {
    return html`
      <div id="${this.rootId}" part="${SbLink.PARTS.root}">
        ${this.prompt?.length
          ? html`
              <p id="${this.promptId}" part="${SbLink.PARTS.prompt}">
                ${this.prompt}
              </p>
            `
          : nothing}

        <rtg-button
          id="${this.buttonId}"
          part="${SbLink.PARTS.button}"
          data-service="${this.service}"
          data-event="${this.event ?? nothing}"
          data-loading="${this.isLoading ? "true" : nothing}"
          variant="link"
          ?disabled="${this.isDisabled}"
        >
          ${this.label}
        </rtg-button>
      </div>
    `;
  }
}
